import React, { useState, useRef, useEffect } from "react";
import styled from "styled-components";
import { constants } from "@thecb/components";
import { noop } from "@/util/common";
import {
  WHITE,
  TEXT_PRIMARY,
  COLOR_SOLID,
  AI_PRIMARY,
  AI_PRIMARY_HOVER,
  AI_ACCENT,
  AI_ACCENT_LIGHT,
  SEARCH_BORDER,
  SEARCH_BORDER_HOVER,
  SEARCH_SURFACE_HOVER,
  SEARCH_PLACEHOLDER,
  ERROR_PRIMARY,
} from "@/constants/colors";
import AiIcon from "@/images/ai-icon.svg";
// The pending mark carries its animation (and its reduced-motion guard) inside
// the asset rather than in a styled-components keyframe (WCHK-3078).
import AiLoadingIcon from "@/images/ai-loading-icon.svg";
import { NARROW_SCREEN } from "./TransactionSearchPage.styled";

// `$color-accent-text` from styles/_variables.scss, shipped by the design
// system under the same value (WCHK-3048).
const { LUCKY_POINT } = constants.colors;

export const NL_SEARCH_STATES = {
  IDLE: "idle",
  PENDING: "pending",
  ERROR: "error",
  SUCCESS: "success",
};

const CHARACTER_LIMIT = 250;
const EMPTY_ERROR = "Search cannot be empty";
const CHARACTER_LIMIT_ERROR =
  "There is a 250 character limit. Remove characters to submit the search.";
const VALIDATION_ERROR_ID = "nl-search-validation-error";
export const EXAMPLE_SEARCHES = [
  "Show successful ACH payments in the last 30 days",
  "Show void payments from yesterday",
];

const EXAMPLES_PANEL_ID = "nl-search-examples";

const SearchBarWrapper = styled.div`
  display: flex;
  align-items: center;
  background: ${WHITE};
  border: 1px solid ${({ $hasError }) => ($hasError ? ERROR_PRIMARY : SEARCH_BORDER)};
  border-radius: 4px;
  padding: 6px 6px 6px 16px;
  gap: 8px;
  min-height: 52px;
  /* One row runs the field, the examples link and the submit button into each
     other on a phone, so they stack (WCHK-3048 review). */
  ${NARROW_SCREEN} {
    flex-direction: column;
    align-items: stretch;
    padding: 12px;
    gap: 12px;
  }
`;

// The input and its mirror must share identical text metrics so the mirrored
// (character-highlighted) text lines up exactly beneath the real caret/text.
const SHARED_TEXT_STYLES = `
  font-size: 1rem;
  font-family: inherit;
  line-height: 1.5;
  letter-spacing: normal;
  padding: 0;
  margin: 0;
  border: none;
  box-sizing: border-box;
`;

const InputStack = styled.div`
  position: relative;
  flex: 1;
  min-width: 120px;
  display: flex;
  align-items: center;
  /* Matches the shared line height, so the mirror and the input keep the same
     box when the browser or the user changes the root font size. */
  height: 1.5rem;
  opacity: ${({ $dimmed }) => ($dimmed ? 0.6 : 1)};
`;

// The mirror is positioned against its nearest positioned ancestor, so the
// text layer — not the whole input stack — has to be that ancestor. Boxing the
// mirror with the icon would paint the query over the icon and start the
// mirrored text an icon-width left of the placeholder (WCHK-2829).
const TextLayer = styled.div`
  position: relative;
  flex: 1;
  min-width: 0;
  height: 100%;
  display: flex;
  align-items: center;
`;

const SearchInput = styled.input`
  ${SHARED_TEXT_STYLES}
  position: relative;
  z-index: 0;
  width: 100%;
  outline: none;
  background: transparent;
  /* Hide the real glyphs so the color-coded mirror shows through, but keep the
     caret visible so typing still feels normal. */
  color: transparent;
  -webkit-text-fill-color: transparent;
  caret-color: ${TEXT_PRIMARY};
  &::placeholder {
    color: ${SEARCH_PLACEHOLDER};
    -webkit-text-fill-color: ${SEARCH_PLACEHOLDER};
  }
  &::selection {
    background: ${AI_ACCENT_LIGHT};
  }
`;

// The mirror paints above the input. The selection highlight belongs to the
// input, so it stays behind the colored glyphs instead of hiding them.
const Mirror = styled.div`
  ${SHARED_TEXT_STYLES}
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1;
  display: flex;
  align-items: center;
  overflow: hidden;
  pointer-events: none;
  color: ${TEXT_PRIMARY};
`;

const MirrorText = styled.span`
  white-space: pre;
`;

const ExcessText = styled.span`
  color: ${ERROR_PRIMARY};
`;

const ValidationMessage = styled.div`
  color: ${ERROR_PRIMARY};
  font-size: 0.875rem;
  line-height: 1.4;
  /* The region stays mounted so screen readers keep one stable announcement
     target. It takes up no space until it holds a message. */
  &:not(:empty) {
    margin-top: 8px;
  }
`;

const AiIconContainer = styled.div`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 0.5rem;

  img {
    display: block;
    /* Both marks render at the container's full 24px rather than at their own
       intrinsic size, which is 16px for the idle asset and 32px for the
       pending one. Sizing here instead of per state keeps the two identical
       in the layout, so swapping them costs no reflow (WCHK-3078). */
    width: 24px;
    height: 24px;
  }

  /* The pending mark ships its animation, and its reduced-motion guard,
     inside the asset: it is loaded through an img element, so a page
     stylesheet cannot reach the keyframes (WCHK-3078). */
`;

// Keeps text in the accessibility tree while taking it off the screen.
const VisuallyHidden = styled.span`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
`;

const ExamplesToggle = styled.button`
  flex-shrink: 0;
  padding: 4px 8px;
  ${NARROW_SCREEN} {
    /* Stacked, the link reads as its own row rather than a trailing scrap. */
    align-self: flex-start;
    padding-left: 0;
  }
  background: transparent;
  border: none;
  font-size: 0.875rem;
  font-family: inherit;
  line-height: 1.4;
  color: ${LUCKY_POINT};
  white-space: nowrap;
  cursor: pointer;
  &:hover:not(:disabled) {
    color: ${AI_PRIMARY_HOVER};
  }
  &:focus-visible,
  &:focus {
    outline: 2px solid ${AI_ACCENT};
    outline-offset: 2px;
  }
  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

// The chips carry the tinted surface, so the row needs no tray of its own
// behind them (WCHK-2829).
const ExamplesPanel = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin-top: 8px;
  padding: 0;
  background: transparent;
  border: none;
`;

const ExampleButton = styled.button`
  padding: 6px 12px;
  background: ${AI_ACCENT_LIGHT};
  border: 1px solid ${AI_ACCENT_LIGHT};
  border-radius: 16px;
  font-size: 0.875rem;
  color: ${COLOR_SOLID};
  cursor: pointer;
  text-align: left;
  font-family: inherit;
  line-height: 1.4;
  &:hover {
    background: ${SEARCH_SURFACE_HOVER};
    border-color: ${SEARCH_BORDER_HOVER};
  }
  &:focus-visible,
  &:focus {
    outline: 2px solid ${AI_ACCENT};
    outline-offset: 2px;
  }
`;

const SubmitButton = styled.button`
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  ${NARROW_SCREEN} {
    /* Stacked, a 40px square at the left edge reads as an orphan. */
    width: 100%;
  }
  background: ${AI_PRIMARY};
  border: none;
  border-radius: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  &:hover:not(:disabled) {
    background: ${AI_PRIMARY_HOVER};
  }
  &:focus {
    outline: 2px solid ${AI_ACCENT};
    outline-offset: 2px;
  }
  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

const SubmitSlot = styled.div`
  flex-shrink: 0;
  ${NARROW_SCREEN} {
    width: 100%;
  }
`;

const SearchIcon = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">
    <circle cx="8" cy="8" r="5.25" stroke="white" strokeWidth="1.5" />
    <path
      d="M12.25 12.25L16 16"
      stroke="white"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
  </svg>
);

const deriveState = ({ pending, error, hasResult }) => {
  if (pending) return NL_SEARCH_STATES.PENDING;
  if (error) return NL_SEARCH_STATES.ERROR;
  if (hasResult) return NL_SEARCH_STATES.SUCCESS;
  return NL_SEARCH_STATES.IDLE;
};

const NaturalLanguageSearch = ({
  pending = false,
  error = null,
  // Whether a translate result landed for the query in the field. The pills
  // show what it populated; nothing renders it here (WCHK-3110).
  hasResult = false,
  onSubmit = noop,
  onExecuteSearch = noop,
  hasParameters = false,
  // Like `hasParameters`, this lets a blank query submit: there is nothing to
  // translate, but there is a new range to search (WCHK-3083 review).
  dateRangeDirty = false,
  // Bumped by the page when a search maps something to the filters, so the
  // field empties. 0 is "nothing to clear".
  clearQueryToken = 0,
}) => {
  const [query, setQuery] = useState("");
  const [lastSubmittedQuery, setLastSubmittedQuery] = useState("");
  const [touched, setTouched] = useState(false);

  // The last submitted query goes too, or typing it again would re-run the old
  // parameters instead of translating it.
  useEffect(() => {
    if (clearQueryToken === 0) return;
    setQuery("");
    setLastSubmittedQuery("");
    setTouched(false);
  }, [clearQueryToken]);
  const mirrorRef = useRef(null);
  const [examplesExpanded, setExamplesExpanded] = useState(false);

  // Keep the mirror's horizontal scroll aligned with the input as text overflows.
  const handleInputScroll = (e) => {
    if (mirrorRef.current) {
      mirrorRef.current.scrollLeft = e.target.scrollLeft;
    }
  };

  const state = deriveState({ pending, error, hasResult });
  const isPending = state === NL_SEARCH_STATES.PENDING;
  const isSuccess = state === NL_SEARCH_STATES.SUCCESS;

  const trimmedQuery = query.trim();

  // Validation restrictions.
  const isOverLimit = query.length > CHARACTER_LIMIT;
  const isBlank = trimmedQuery.length === 0;
  // A blank query is searchable when there are parameters to search against,
  // or a range the user changed since the last search.
  const blankCanSearch = hasParameters || dateRangeDirty;
  // Blank is only a violation once the user has interacted and there is
  // nothing for a blank submit to search.
  const showBlankError = touched && isBlank && !blankCanSearch;

  const validationError = isOverLimit
    ? CHARACTER_LIMIT_ERROR
    : showBlankError
      ? EMPTY_ERROR
      : null;

  const hasValidationError = Boolean(validationError);

  // In SUCCESS state, run the search only if input matches the last-submitted
  // query. If user edits the input, re-translate first (else stale results).
  const inputMatchesLastSubmit = isSuccess && trimmedQuery === lastSubmittedQuery;
  const canInterpret =
    !isPending && !isOverLimit && (trimmedQuery.length > 0 || blankCanSearch);
  const canExecute = !isPending && !isOverLimit;
  // Submits that skip the translate step and search directly: a blank query
  // has nothing to interpret, and a repeat of the last translated query was
  // interpreted already.
  const runsDirectly = inputMatchesLastSubmit || (isBlank && blankCanSearch);

  const handleChange = (e) => {
    setQuery(e.target.value);
    setTouched(true);
  };

  const handlePrimaryAction = () => {
    setTouched(true);
    if (isPending || isOverLimit) return;
    if (runsDirectly) {
      if (canExecute) onExecuteSearch();
      setExamplesExpanded(false);
      return;
    }
    if (!canInterpret) return;
    setExamplesExpanded(false);
    setLastSubmittedQuery(trimmedQuery);
    onSubmit(trimmedQuery);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handlePrimaryAction();
    }
  };

  // Picking the example that is already the last submitted query would re-run
  // the same translate round trip for the same answer, so it is a no-op
  // (WCHK-3048).
  const handleExampleClick = (example) => {
    if (example !== lastSubmittedQuery) {
      setExamplesExpanded(false);
      setQuery(example);
      setLastSubmittedQuery(example);
      onSubmit(example);
    }
  };

  const submitAriaLabel = runsDirectly ? "Run search" : "Submit AI search";
  const submitDisabled = runsDirectly ? !canExecute : !canInterpret;

  return (
    <div
      data-qa="nl-search-container"
      data-testid="nl-search-container"
      data-state={state}
      style={{ width: "100%", marginBottom: "16px" }}
    >
      <SearchBarWrapper
        $hasError={hasValidationError}
        data-qa="nl-search-bar"
        data-testid="nl-search-bar"
      >
        <InputStack
          $dimmed={isPending}
          data-qa="nl-search-input-stack"
          data-testid="nl-search-input-stack"
        >
          <AiIconContainer
            data-qa={isPending ? "nl-search-spinner" : "nl-search-icon"}
            data-testid={isPending ? "nl-search-spinner" : "nl-search-icon"}
          >
            {/* Decoration only. The input owns the accessible name, so the
                mark carries no alt and stays out of the accessibility tree;
                while a search runs the animated mark takes over, but motion
                alone is not an announcement — the status region below is the
                signal (WCHK-3048 review). */}
            <img
              src={isPending ? AiLoadingIcon : AiIcon}
              role="presentation"
              aria-hidden="true"
            />
            {/* The status region stays mounted so screen readers see the
                "Searching" text arrive as a change inside an existing live
                region; a region inserted with its content pre-populated is
                often not announced at all (WCHK-3078 review). */}
            <VisuallyHidden role="status">
              {isPending ? "Searching" : ""}
            </VisuallyHidden>
          </AiIconContainer>
          <TextLayer
            data-qa="nl-search-text-layer"
            data-testid="nl-search-text-layer"
          >
            <Mirror ref={mirrorRef} aria-hidden="true">
              <MirrorText>
                {query.slice(0, CHARACTER_LIMIT)}
                <ExcessText
                  data-qa="nl-search-excess"
                  data-testid="nl-search-excess"
                >
                  {query.slice(CHARACTER_LIMIT)}
                </ExcessText>
              </MirrorText>
            </Mirror>
            <SearchInput
              data-qa="nl-search-input"
              data-testid="nl-search-input"
              id="nl-search-input"
              placeholder="Search Payments"
              value={query}
              onChange={handleChange}
              onKeyDown={handleKeyDown}
              onScroll={handleInputScroll}
              aria-label="Natural language search"
              disabled={isPending}
              aria-invalid={hasValidationError}
              aria-describedby={hasValidationError ? VALIDATION_ERROR_ID : undefined}
            />
          </TextLayer>
        </InputStack>

        <ExamplesToggle
          type="button"
          data-qa="nl-search-examples-toggle"
          data-testid="nl-search-examples-toggle"
          onClick={() => setExamplesExpanded((expanded) => !expanded)}
          aria-expanded={examplesExpanded}
          aria-controls={examplesExpanded ? EXAMPLES_PANEL_ID : undefined}
          disabled={isPending}
        >
          {examplesExpanded ? "Hide example searches" : "View example searches"}
        </ExamplesToggle>

        <SubmitSlot data-qa="nl-search-submit" data-testid="nl-search-submit">
          <SubmitButton
            onClick={handlePrimaryAction}
            disabled={submitDisabled}
            type="button"
            aria-label={submitAriaLabel}
          >
            <SearchIcon />
          </SubmitButton>
        </SubmitSlot>
      </SearchBarWrapper>

      <ValidationMessage
        id={VALIDATION_ERROR_ID}
        aria-live="polite"
        aria-atomic="true"
        data-qa="nl-search-validation-error"
        data-testid="nl-search-validation-error"
      >
        {validationError}
      </ValidationMessage>

      {examplesExpanded && (
        <ExamplesPanel
          id={EXAMPLES_PANEL_ID}
          data-qa="nl-search-examples"
          data-testid="nl-search-examples"
        >
          {EXAMPLE_SEARCHES.map((example) => (
            <ExampleButton
              key={example}
              type="button"
              data-qa="nl-search-example"
              data-testid="nl-search-example"
              onClick={() => handleExampleClick(example)}
            >
              {example}
            </ExampleButton>
          ))}
        </ExamplesPanel>
      )}
    </div>
  );
};

export default NaturalLanguageSearch;
